import * as React from 'react'
import { createContext, PropsWithChildren, ReactElement } from 'react'
import i18n, { TFunction } from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next, useTranslation } from 'react-i18next'
import resourcesToBackend from 'i18next-resources-to-backend'
import * as Yup from 'yup'
import * as YupLocales from 'yup-locales'

export type I18NContextType = {
    setLanguage: (language: string) => void
}

const I18NContext = createContext<I18NContextType>(undefined)

/**
 * Builds the Yup Locale
 */
export function buildYupLocale(locale: string): void {
    Yup.setLocale(YupLocales[locale] ? YupLocales[locale] : YupLocales.nl)
}

export function initializeI18N(): Promise<TFunction> {
    return (
        i18n
            // detect user language
            // learn more: https://github.com/i18next/i18next-browser-languageDetector
            .use(LanguageDetector)
            // pass the i18n instance to react-i18next.
            .use(initReactI18next)
            .use(resourcesToBackend((language, namespace) => import(`../../assets/i18n/${language}/${namespace}.json`)))
            // init i18next
            // for all options read: https://www.i18next.com/overview/configuration-options
            .init(
                {
                    fallbackLng: 'nl',
                    supportedLngs: ['en', 'nl', 'cimode'],
                },
                () => buildYupLocale(i18n.language),
            )
    )
}

export function I18NProvider(props: PropsWithChildren): ReactElement {
    const { t: translate, i18n } = useTranslation()

    return (
        <I18NContext.Provider
            value={{
                setLanguage: (newLng: string) => {
                    if (translate && i18n.isInitialized && i18n.language !== newLng) {
                        window.localStorage?.setItem('i18nextLng', newLng)
                        i18n.changeLanguage(newLng)
                        buildYupLocale(newLng)
                    }
                },
            }}
        >
            {props.children}
        </I18NContext.Provider>
    )
}

export default I18NContext
